/**
 * ALL game balance lives here — no magic numbers in systems (hard rule).
 * The dev overlay (F3) live-edits the most-tuned values at runtime.
 *
 * DECISION: attrition tuned to ~1 hp/s against a 100 hp pool (100s standing
 * still on full health, before food). Well below quarter-eating arcade rates;
 * goal is tension, not punishment. Expect playtest retune — edit live via F3.
 */
export const TILE = 32

export const CONFIG = {
  player: {
    maxHp: 100,
    speed: 150,
    radius: 11,
    lives: 3,
    startMagic: 2,
    maxMagic: 5,
    /** playtest: the starting weapon carried the whole campaign — it now
     *  starts under-provisioned and the requisition tracks are the ladder */
    fireCooldown: 0.2,
    projectileSpeed: 440,
    projectileDamage: 7,
    projectileRadius: 4,
    contactDamageCooldown: 0.45,
    invulnAfterHit: 0.35,
    invulnAfterSpawn: 2.0,
    /** hp after losing a life — partial, so dying is never a heal strategy */
    respawnHp: 60,
    /** seconds between losing a life and re-entering the floor */
    respawnDelay: 1.6,
    /**
     * door-approach assist (playtest: "I get stuck on the doors if I'm not
     * perfectly lined up"): moving into a doorway within tolerance px of its
     * centerline steers the blocked lateral component toward the opening
     * instead of stopping dead on the frame. Player-only; pullSpeed in px/s.
     */
    doorAssist: { tolerance: 22, pullSpeed: 170 }
  },
  attrition: {
    hpPerSecond: 1.0,
    starvingThreshold: 25,
    /** floors deeper than this add hpPerSecond * depthScale * (floor-1) */
    depthScale: 0.04
  },
  food: { heal: 30 },
  magic: { screenDamage: 60 },
  projectiles: {
    /** seconds a bolt lives before expiring in flight (player and network) */
    lifetime: 2.2
  },
  hazards: {
    trapDamage: 10,
    /** seconds between trap ticks while standing on the plate */
    trapCooldown: 1.0,
    /** shots to crumble a destructible wall */
    destructibleWallHp: 3
  },
  enemies: {
    /** enemy speed scales by difficulty band: speed *= 1 + band * speedBandScale (never the auditor) */
    speedBandScale: 0.05,
    /**
     * dormancy (playtest: whole rooms locked on through walls and piled at
     * the door): hostiles idle until the participant is seen (LOS within
     * sightRange), heard close (proximity), or a packmate within packRadius
     * is shot. Statics and the Auditor ignore this — born aware.
     */
    wake: { sightRange: 300, proximity: 110, packRadius: 150 },
    husk: { hp: 12, speed: 92, contactDamage: 8, radius: 10, score: 10 },
    sentry: {
      hp: 26,
      speed: 68,
      contactDamage: 6,
      radius: 11,
      score: 25,
      projectileSpeed: 230,
      projectileDamage: 10,
      fireCooldown: 1.6,
      preferredRange: 210,
      retreatRange: 130
    },
    static: { hp: 10, speed: 58, contactDamage: 12, radius: 10, score: 20 },
    foreman: {
      hp: 38,
      speed: 55,
      contactDamage: 6,
      radius: 12,
      score: 60,
      auraRadius: 150,
      spawnRateMultiplier: 2.2,
      resurrectCooldown: 4.0,
      resurrectRadius: 130
    },
    auditor: {
      speed: 42,
      drainPerSecond: 26,
      radius: 13,
      score: 150,
      /** stalling is not a plan: speed = speed + levelTime * rampPerSecond, capped under player speed */
      rampPerSecond: 0.75,
      rampCap: 140
    }
  },
  generators: {
    /** playtest: assemblers fell too easily and floors ran thin — more hp,
     *  faster cycling, bigger standing crowds */
    baseHp: 70,
    spawnInterval: 2.2,
    /** max simultaneously-alive children per generator */
    aliveCap: 7,
    pulsePeriod: 1.2,
    score: 100,
    /** generator hp scales by difficulty band: hp = baseHp * (1 + band * hpBandScale) */
    hpBandScale: 0.18,
    spawnIntervalBandScale: 0.08,
    /** food pickups dropped on destruction — killing assemblers buys time against attrition */
    foodDrop: 1
  },
  /**
   * AMENITIES: run-scoped perks on the between-floor panel (defs and stock
   * rules in src/game/amenities.ts). One total approval per interval across
   * both shelves. Residuals bank at run end and buy permanent stock adds.
   */
  amenities: {
    /** score price per amenity (run-scoped) */
    prices: { rupture: 800, metabolism: 700, invoice: 600, momentum: 900, lingering: 900, surcharge: 700 },
    /** residuals banked at run end = floor(final score * residualRate) */
    residualRate: 0.02,
    /** residual cost of each successive permanent stock unlock */
    unlockCosts: [150, 300, 500],
    /** RUPTURE CLAUSE: shrapnel pellets per kill */
    rupture: { pellets: 4, damage: 4, speed: 260, lifetime: 0.22 },
    /** SURPLUS METABOLISM: overflow-to-shield conversion and decay */
    metabolism: { convertRate: 0.5, shieldCap: 40, decayPerSecond: 2 },
    /** DEAD MAN'S INVOICE: fraction of run spending refunded per life lost */
    invoice: { refundRate: 0.25 },
    /** MOMENTUM RIDER: OVERRIDE charge-per-kill multiplier */
    momentum: { chargeMul: 1.6 },
    /** LINGERING SANCTION: field left by a charge detonation */
    lingering: { radius: 110, seconds: 3, tickInterval: 0.4, tickDamage: 6 },
    /** IMPACT SURCHARGE: heavy-shot cooldown multiplier */
    surcharge: { cooldownMul: 0.6 }
  },
  /**
   * OVERRIDE: kill-charged slow-motion. The meter fills on kills; a full
   * meter activates timeScale on the LOOP's accumulator feed (tick dt and
   * tick math untouched — determinism per seed holds), drains over
   * maxSeconds of real time, then cools down so it cannot be chained.
   */
  override: {
    /** kills to fill the meter from empty */
    killsToFill: 8,
    /** sim advances at this fraction of real time while active */
    timeScale: 0.4,
    /** real seconds of slow-motion per full meter */
    maxSeconds: 2.5,
    /** real seconds after depletion before the meter charges again */
    cooldown: 6,
    /** music sequencer step multiplier while active (tempo drop) */
    musicTempoMul: 1.5
  },
  /**
   * Sanctioned armaments: temporary weapons with limited charge, dropped by
   * assemblers on destruction and rarely issued on floor entry. They are a
   * SPIKE above the requisition ladder, not a replacement: requisition
   * damage/fire-rate ranks apply to the standard-issue blaster only —
   * armaments use these fixed profiles and revert to the blaster when spent.
   */
  armaments: {
    /** chance an assembler drops an armament on destruction (level RNG stream) */
    dropChance: 0.22,
    /** chance a standard floor is seeded with one armament at a far floor tile */
    floorIssueChance: 0.3,
    /** SCATTER: short-range pellet spread (lifetime caps the reach) */
    scatter: { charge: 16, fireCooldown: 0.5, damage: 5, pellets: 5, spreadRad: 0.55, projectileSpeed: 380, lifetime: 0.4 },
    /** LANCE: piercing rail bolt, slow cycle */
    lance: { charge: 10, fireCooldown: 0.9, damage: 26, projectileSpeed: 720 },
    /** SEAM: ricochet bolt — bounces off walls */
    seam: { charge: 14, fireCooldown: 0.35, damage: 9, projectileSpeed: 430, bounces: 3 },
    /** SUPPRESSOR: very fast cycle, low per-bolt damage */
    suppressor: { charge: 40, fireCooldown: 0.08, damage: 4, projectileSpeed: 520 },
    /**
     * standard-blaster alt-fire (E / touch HEAVY): heavier, slower bolt on its
     * own cooldown; it IS the blaster, so requisition mods apply on top
     */
    altFire: { cooldown: 2.2, damageMul: 3.5, speedMul: 0.55, radius: 7 }
  },
  /**
   * Between-floor requisitions: permanent-for-the-run upgrades bought with
   * score. This IS the weapon progression (playtest: the old base weapon
   * carried the campaign unupgraded): the participant starts under-provisioned
   * and buys back up. Prices sit against the floor economy (a good floor
   * settles around 800-1400 with bonuses): rank 1 lands around floor 2; all
   * 16 ranks cost 25600 — beyond any 15-floor campaign gross, so builds are
   * choices, not checklists.
   */
  requisition: {
    /** purchasable ranks per track per run */
    maxRanks: 4,
    /** performance-index price of each successive rank (same curve on every track) */
    priceRank1: 500,
    priceRank2: 1100,
    priceRank3: 1900,
    priceRank4: 2900,
    /** CYCLING PERMIT: fire cooldown multiplier drops this much per rank (additive: rank 4 = 0.56x) */
    fireCooldownCutPerRank: 0.11,
    /** AMPLIFIED MUNITIONS: projectile damage multiplier gains this much per rank (additive: rank 4 = 2x) */
    damageGainPerRank: 0.25,
    /** REINFORCED CHASSIS: max vitals added per rank (approval also restores this much) */
    hpPerRank: 20,
    /** EXPANDED AUTHORIZATION: sanctioned charge capacity per rank (approval issues one charge) */
    chargesPerRank: 1
  },
  score: {
    treasure: 250,
    /** archive-fragment pickup credit — fixed per pickup so per-seed scores stay
     *  deterministic regardless of which records the profile already owns */
    archiveRecord: 150,
    levelClear: 500,
    bossKill: 2500,
    auditorRemoved: 150,
    /** floor-clear settlement bonuses (awarded on level_clear, shown on the floor card) */
    noDamageBonus: 300,
    /** every assembler on the floor destroyed — even where the exit never required it */
    decommissionBonus: 200,
    /** cleared under par time */
    expeditionBonus: 250,
    /** par time in seconds: parBaseSeconds + parPerActSeconds * (act - 1) */
    parBaseSeconds: 75,
    parPerActSeconds: 25
  },
  boss: {
    /** playtest (souls player, granted): bosses fell fast and hit soft —
     *  more hp, harder hits, tighter telegraph/recovery windows */
    telegraphTime: 0.8,
    recoveryTime: 0.65,
    chargeSpeed: 520,
    contactDamage: 26,
    baseHp: 900,
    hpPerAct: 450,
    radius: 26,
    touchCooldown: 0.6,
    /** slam dead zone: no damage within (boss radius + this) — point-blank slams are escapable */
    slamInnerSafe: 20,
    /** slam defaults when a BossDef doesn't override via params */
    slamRadius: 120,
    slamDamage: 28,
    /** chance a boss-spawned add drops a ration — long fights stay survivable */
    addFoodDropChance: 0.3,
    /**
     * boss attacks erode breakable cover (playtest: pillars were effectively
     * permanent). Units are destructible-wall hp ticks (player-facing HP in
     * hazards.destructibleWallHp stays untouched): charge = per pillar in the
     * charge path (3 shatters outright — the Director smashes through);
     * slam = per slam within the shockwave (1 → three slams to crack cover,
     * so the arena erodes over the fight rather than in the opening seconds)
     */
    wallDamage: { charge: 3, slam: 1 }
  },
  feel: {
    hitFlashTime: 0.09,
    shakeBig: 9,
    shakeSmall: 3.5,
    /** tiny kick on every player shot */
    shakeFire: 0.4,
    shakeDecay: 22,
    particleCount: 12,
    telegraphGlow: 0.85,
    /** pixels a struck enemy is nudged along the shot direction (never bosses or the auditor) */
    hitKnockback: 2.5,
    /**
     * Off-screen objective chevron (renderer): points at the open exit, or at
     * the nearest living assembler while production still seals the exit.
     * Sizes are CSS px; reduced motion pins alpha at chevronAlpha (no pulse).
     */
    chevronSize: 9,
    chevronMargin: 30,
    chevronPulseHz: 1.6,
    chevronAlpha: 0.9,
    chevronMinAlpha: 0.5
  },
  daise: {
    lineCooldown: 3.5,
    lineDuration: 5.0,
    starvingRepeatEvery: 22,
    /** first-ever run has no best score to beat — celebrate crossing this instead */
    firstRunScoreLine: 500,
    /** live-commentary client knobs (issue #38) — the bank stays the instant path */
    live: {
      /** at most 1 in N presented lines is live (plain counter, no RNG) */
      liveEvery: 3,
      /** prefetch when fewer than this many candidates are buffered */
      lowWater: 2,
      /** buffered-candidate cap */
      poolCap: 6,
      /** min seconds between snapshot requests */
      requestCooldown: 8,
      /** client-side abort (ms) — the Worker holds its own upstream timeouts */
      fetchTimeoutMs: 4000,
      reportTimeoutMs: 8000
    }
  },
  /** attract mode (issue #45): the self-playing broadcast behind the main menu */
  attract: {
    /** authored floors the loop cycles through (5 is a featured-encounter gate) */
    floorPool: [2, 3, 4, 7, 5, 8],
    /** hard cap per loop before recycling to the next floor (s) */
    loopSeconds: 38,
    /** how long the camera lingers on an expired participant before recycling (s) */
    deathLinger: 1.6,
    /** bot skirmish band: close beyond far, retreat inside near, strafe between (px) */
    bandFar: 210,
    bandNear: 120,
    fireRange: 430,
    /** field-spell panic trigger: N hostiles inside the radius, edge-limited (s) */
    panicRadius: 130,
    panicCount: 3,
    magicCooldown: 9
  },
  /**
   * Audio presentation: music scheduler + mix knobs. Synth recipes (pitch
   * content, envelopes) live in src/audio/manager.ts; every tempo/volume
   * number routes through here.
   */
  audio: {
    /** lookahead scheduler: notes queue this far ahead of the context clock (s) */
    musicScheduleAhead: 0.2,
    /** wall-clock scheduler wakeup (ms) — throttled ticks just queue further ahead */
    musicSchedulerInterval: 100,
    /** music gain multiplier while paused (pre-run menus keep current behavior) */
    pauseDuckMul: 0.4,
    /** duck/restore ramp time constant (s) */
    duckRampSeconds: 0.12,
    /** low heartbeat double-thump on each player_starving event (event cadence only) */
    starveThumpVolume: 0.4,
    /** per-pattern sequencer tempo (s per step) and gain multiplier; note tables in the manager */
    music: {
      act1: { step: 0.28, gain: 1 },
      act2: { step: 0.26, gain: 1 },
      act3: { step: 0.24, gain: 1.05 },
      boss: { step: 0.32, gain: 1.15 }
    }
  },
  levels: {
    perAct: 5, // 4 standard + 1 boss gate; every 5th floor is a boss
    authoredCount: 15
  },
  /** procedural-generator layout knobs (structure only — budgets scale off band) */
  procgen: {
    /** relative weights for the standard-floor layout archetypes */
    archetypeChainWeight: 2,
    archetypeHubWeight: 1.2,
    archetypeRingWeight: 1.2,
    /** chance each room/corridor junction choke receives a door tile */
    doorChance: 0.7,
    /** chance a reach-exit HUB floor becomes a vault: locked exit spoke, key in another spoke */
    vaultChance: 0.35
  },
  /**
   * ARCHIVE fragments + ending vignettes. Fragment content lives in
   * src/story/archive.ts; these are the balance/pacing knobs.
   */
  archive: {
    /** chance a standard procedural floor places a fragment in its farthest room */
    procgenChance: 0.25,
    /** seconds between forced feed lines during the ending vignette */
    vignetteLineInterval: 2.6,
    /** hold after the vignette's last line before the win screen appears */
    vignetteHold: 4.0
  },
  /**
   * Presentation-side story knobs: signal intrusions (src/story/intrusions.ts)
   * and the Provisioner (src/story/provisioner.ts).
   */
  story: {
    /** chance an eligible floor transition (act 2+, non-boss) is cut into by the signal */
    intrusionChance: 0.12,
    /** first act eligible for the random intrusion roll */
    intrusionMinAct: 2,
    /** current index at/above this draws the Provisioner's high-score lines into the pool */
    provisionerHighScore: 8000,
    /** lifetime runs at/above this make the participant one of the Provisioner's regulars */
    provisionerVeteranRuns: 8
  },
  /**
   * Overtime directives: endless-only per-floor modifiers, one rolled per
   * standard floor (deterministic per seed+floor; boss gates stay clean).
   * Each directive is a bundle of tuning overrides applied for that floor.
   */
  endless: {
    /** first endless floor eligible for a directive */
    directiveMinFloor: 2,
    directives: {
      /** +score from all sources, but assemblers cycle faster */
      surplus: { scoreMul: 1.5, generatorIntervalMul: 0.75 },
      /** rations heal half, but extra stock is placed on the floor */
      rationing: { foodHealMul: 0.5, extraFood: 1 },
      /** every hostile asset moves faster; premium rates apply */
      escalation: { enemySpeedMul: 1.15, scoreMul: 1.25 },
      /** an (extra) audit begins this many seconds in, regardless of schedule */
      earlyAudit: { auditorDelay: 20 },
      /** visibility reduced to a radius around the participant */
      blackout: { scoreMul: 1.25, radiusTiles: 6, maxDarkness: 0.9 }
    }
  }
} as const

/** Mutable runtime copy for the dev overlay to live-edit. */
export type Tuning = {
  -readonly [K in keyof typeof CONFIG]: { -readonly [P in keyof (typeof CONFIG)[K]]: (typeof CONFIG)[K][P] }
}

export function makeTuning(): Tuning {
  return structuredClone(CONFIG) as unknown as Tuning
}
